import { useQuery } from "@tanstack/react-query";
import { CTA, styled, Text, TokenTextAppearance, TextTag } from "@volkswagen-onehub/components-core";
import { Offers } from "@volkswagen-onehub/icons-core";
import { FocusLayerSizeV2 } from "@volkswagen-onehub/layer-manager";
import { formatPercentage } from "@vw-marketing/vwca-utils";
import React, { useContext, useEffect, useState } from "react";
import { DisclaimerMarker } from "../../components/DisclaimerMarker";
import OffersModal from "../../components/Offers/OffersModal";
import { LayerContext, useFocusLayer } from "../../Layers";
import { getLang, getTranslations } from "../../translations";
import { ModelKey, Offer } from "../../types/offers";
import { useFeatureServices } from "../../use-feature-services";
import { fetchOffers } from "../../api/fetchOffers";
import { formatCopy } from "../../utils/format-copy";
import getModelKey from "../../utils/get-model-key";
import { getOffer } from "../../utils/get-offer";
import { createConfiguration, createConstantData, createPartner, createProcess } from "../../utils/get-tracking-event-data";
import { Language, Province, VehicleData } from "../../types";
import { PaymentType } from "@vw-marketing/vw-ca-types";
import { VehicleDetailsContext } from "../../utils/details-context";
import { AppContext } from "../../app-context";

const LeadOfferWrapper = styled.div`
  padding: ${props => props.theme.size.dynamic0100};
  background-color: #FFD100;
  display: flex;
  column-gap: 16px;
  @media screen and (min-width: 1040px) {
    grid-row: 2 / span 1;
    grid-column: 2 / 3;
  }
`;

const TextContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  grid-row-gap: 16px;
  justify-items: start;
`;

const StyledText = styled(Text)`
  display: flex;
  flex-direction: column;
`;

const StyledCTA = styled(CTA)`
`;

interface Props {
  vehicle: VehicleData;
}

export default ({ vehicle }: Props) => {
  const lang = getLang();
  const { offersCopy } = getTranslations();

  const [ leadOffer, setLeadOffer ] = useState<Offer | null>(null);
  const [ bonusOffer, setBonusOffer ] = useState<{copy: string, legal: string} | null>(null);
  const { closeLayer } = useContext(LayerContext);

  const { globalApi } = useContext(AppContext);
  const context = useContext(VehicleDetailsContext);
  const dealer = context.dealer;

  const { tracking } = useFeatureServices();
  const openOffersLayer = useFocusLayer('offers-layer', {
    size: FocusLayerSizeV2.C,
    onClose: () => closeLayer('offers-layer')
  });

  if (!vehicle || vehicle.category !== "new") return null;

  const dealerProvince = vehicle.dealer_info.province;

  const { data: trimOnOffer } = useQuery({
    queryKey: ['offers', dealerProvince],
    queryFn: () => {
      return fetchOffers(globalApi, lang, {
        province: dealerProvince as Province
      });
    },
    select: (data) => getOffer(data, getModelKey(vehicle.model_name) as ModelKey, String(vehicle.year), vehicle.model_code),
    enabled: !!dealerProvince
  });

  useEffect(() => {
    if (trimOnOffer) {
      const leadType = trimOnOffer?.trims[0].default_payment;
      const offer = trimOnOffer.trims[0].offers.filter(x => x.type === leadType)[0];
      setLeadOffer(offer);
    }
  }, [trimOnOffer]);

  useEffect(() => {
    if (leadOffer && leadOffer.bonus_offer && leadOffer.bonus_offer_legal) {
      setBonusOffer({
        copy: leadOffer.bonus_offer,
        legal: leadOffer.bonus_offer_legal
      });
    }
  }, [leadOffer]);


  if (!trimOnOffer) return null;
  if (!leadOffer) return null;

  const handleClick = () => {
    openOffersLayer((close) => <OffersModal close={close} source={["inventory", "vehicle details page"]} offer={trimOnOffer} vehicle={vehicle} dealer={dealer}/>)

    tracking.track('VWBasic_VehicleButton_Click', {
      ...createConstantData('VWBasic_VehicleButton_Click', vehicle.category),
      configuration: createConfiguration('VWBasic_VehicleButton_Click', vehicle, lang),
      partner: createPartner(dealer || undefined),
      process: createProcess("3"),
      link: {
        url: window.location.href,
        name: viewOffers,
        isDealerPage: false
      }
    });
  };
  const { bonusCash, deadline, andPay, plusTaxes, atRate, term, withDownpayment, viewOffers } = offersCopy;

  return (
    <LeadOfferWrapper>
      <Offers variant="large" ariaHidden />
      <TextContainer>
        {
          leadOffer?.type === PaymentType.CASH
          ? <StyledText appearance={TokenTextAppearance.copy200}>
            <Text bold>
              { bonusCash.replace("%%bonus_cash%%", "$3,000") }
            </Text>
            <Text>
              { deadline[leadOffer?.type as PaymentType].replace("%%end_date%%", leadOffer?.end_date) }
            </Text>
          </StyledText>
          : <StyledText appearance={TokenTextAppearance.copy200}>
            <Text>
              <Text bold>
                { deadline[leadOffer?.type as PaymentType].replace("%%payment_type%%", leadOffer?.label).replace("%%end_date%%", leadOffer?.end_date)}
              </Text>
              { ` ${andPay}` }
            </Text>

            <Text>
              <Text bold>
                {`${leadOffer?.payment} ${leadOffer?.frequency}`}
              </Text>
              { ` ${plusTaxes}` }
            </Text>

            <Text>
              <Text bold>
                { `${atRate.replace("%%rate%%", formatPercentage(lang, leadOffer?.rate || 0))}` }
              </Text>
              { ` ${term.replace("%%term%%", leadOffer?.term)}` }
            </Text>

            <Text>
              { withDownpayment.replace("%%down_pay%%", leadOffer?.downpayment)}
              { lang === Language.FR ? " " : "" }
              <DisclaimerMarker marker="††" text={leadOffer.legal} id={`${vehicle.vin}-${leadOffer.type}-offer`} />
            </Text>
          </StyledText>
        }

        {
          bonusOffer?.copy
          ? <Text>
            { formatCopy(bonusOffer?.copy) }
            { lang === Language.FR ? " " : "" }
            <Text tag={TextTag.sup}>‡‡</Text>
          </Text>
          : null
        }

        <StyledCTA
          tag="button"
          onClick={handleClick}
          emphasis="tertiary"
        >
          { viewOffers }
        </StyledCTA>
      </TextContainer>
    </LeadOfferWrapper>
  );
};
